"use client";

import { Badge } from "@repo/ui/components/ui/badge";
import { Trace, TraceDescription, TraceIcon, TraceLine, TraceTitle } from "@repo/ui/components/ui/trace";
import { FileTextIcon } from "lucide-react";
import type { ToolPart } from "../turn";
import { Step, StepCodeBlock, stepStatus } from "./trace-step";

/** `fhir_query`'s arguments, as the engine's tool declares them
 * (services/ai-engine/src/agents/tools/fhir.py). `params` is a JSON string. */
interface FhirQueryInput {
  resource_type?: string;
  action?: string;
  params?: string;
  resource_id?: string;
}

type FhirResource = { resourceType?: string; id?: string } & Record<string, unknown>;

const VERBS: Record<string, { running: string; done: string; failed: string }> = {
  search: { running: "Searching", done: "Searched", failed: "Failed to search" },
  count: { running: "Counting", done: "Counted", failed: "Failed to count" },
  read: { running: "Reading", done: "Read", failed: "Failed to read" },
};

function parseJson<T>(text: string): T | undefined {
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined;
  }
}

function asInput(input: unknown): FhirQueryInput {
  return typeof input === "object" && input !== null ? (input as FhirQueryInput) : {};
}

function searchParams(input: FhirQueryInput): [string, string][] {
  const parsed = input.params ? parseJson<Record<string, unknown>>(input.params) : undefined;
  if (!parsed || typeof parsed !== "object") return [];
  return Object.entries(parsed).map(([key, value]) => [key, String(value)]);
}

/** The engine's search result: a "Matched …" header, then one compact JSON
 * resource per line between `[` and `]`, then an optional NOTE for the model.
 * A read returns the resource as bare JSON. Anything else is shown verbatim. */
function parseOutput(action: string | undefined, output: string): { header?: string; resources?: FhirResource[] } {
  if (action === "read") {
    const resource = parseJson<FhirResource>(output);
    return resource?.resourceType ? { resources: [resource] } : {};
  }
  if (action !== "search") return { header: output };

  if (output.startsWith("Matched 0")) return { header: "No matches" };

  const start = output.indexOf("\n[\n");
  const end = output.indexOf("\n]", start);
  if (start === -1 || end === -1) return { header: output };

  const resources = output
    .slice(start + 3, end)
    .split("\n")
    .map((line) => parseJson<FhirResource>(line.replace(/,$/, "")))
    .filter((r): r is FhirResource => !!r?.resourceType);
  return { header: matchedSummary(output.slice(0, start), resources.length), resources };
}

/** The engine's header is instructions to the model ("do NOT recount…");
 * a reader needs only how many matched and how many are shown. */
function matchedSummary(header: string, shown: number): string {
  const total = header.match(/^Matched (\d+) total/)?.[1];
  if (total === undefined) return `${shown} returned`;
  return Number(total) > shown ? `${total} matched, ${shown} shown` : `${total} matched`;
}

const firstString = (...values: unknown[]) => values.find((v): v is string => typeof v === "string" && v.length > 0);

function codeText(value: unknown): string | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const concept = value as { text?: unknown; coding?: { display?: unknown; code?: unknown }[] };
  return firstString(concept.text, concept.coding?.[0]?.display, concept.coding?.[0]?.code);
}

function humanName(value: unknown, patient: boolean): string | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;
  const name = (patient ? (value.find((name) => name?.use === "official") ?? value[0]) : value[0]) as {
    text?: unknown;
    given?: unknown[];
    family?: unknown;
  };
  return firstString(name.text, [...(name.given ?? []), name.family].filter(Boolean).join(" "));
}

/** One line per resource: what it is, then when and in what state. Fields are
 * the common FHIR ones; a resource with none of them reads as its type and id. */
function describe(resource: FhirResource): { title: string; detail?: string } {
  const r = resource as Record<string, unknown>;
  const period = r.period as { start?: unknown } | undefined;
  const label = firstString(
    humanName(r.name, r.resourceType === "Patient"),
    typeof r.name === "string" ? r.name : undefined,
    r.title,
    codeText(r.code),
    codeText(r.serviceType),
    codeText(r.type),
    r.description,
  );
  const date = firstString(r.start, period?.start, r.effectiveDateTime, r.authoredOn, r.date, r.birthDate);
  const detail = [r.status, date?.slice(0, 10)].filter(Boolean).join(" · ");
  return {
    title: label ? `${resource.resourceType} · ${label}` : `${resource.resourceType}/${resource.id ?? "?"}`,
    detail: detail || undefined,
  };
}

/** `fhir_query` as a Chain of Thought step: the query as badges, the result
 * as one row per resource rather than the JSON the model reads. */
export function FhirQueryStep({ part, streaming }: { part: ToolPart; streaming: boolean }) {
  const input = asInput(part.input);
  const status = stepStatus(part, streaming);
  const verbs = VERBS[input.action ?? ""] ?? { running: "Querying", done: "Queried", failed: "Failed to query" };
  const verb = status === "running" ? verbs.running : status === "error" ? verbs.failed : verbs.done;
  const target =
    input.action === "read" && input.resource_id
      ? `${input.resource_type}/${input.resource_id}`
      : (input.resource_type ?? "FHIR");
  const title = `${verb} ${target}`;

  if (status === "running") return <Step status={status} title={title} />;

  const params = searchParams(input);
  const output = part.state === "done" && typeof part.output === "string" ? part.output : undefined;
  const { header, resources } = output ? parseOutput(input.action, output) : {};

  return (
    <Step status={status} title={title}>
      {params.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {params.map(([key, value]) => (
            <Badge key={key} variant="secondary" className="font-mono">
              {key}={value}
            </Badge>
          ))}
        </div>
      )}
      {part.state === "error" && <StepCodeBlock value={part.error} destructive />}
      {header && <p className="text-muted-foreground text-xs">{header}</p>}
      {resources && resources.length > 0 && (
        <Trace>
          {resources.map((resource, index) => {
            const { title: rowTitle, detail } = describe(resource);
            return (
              // Ids can repeat across types; position within one result does not.
              <TraceLine key={`${resource.resourceType}-${resource.id ?? index}`}>
                <TraceIcon>
                  <FileTextIcon />
                </TraceIcon>
                <TraceTitle>{rowTitle}</TraceTitle>
                {detail && <TraceDescription>{detail}</TraceDescription>}
              </TraceLine>
            );
          })}
        </Trace>
      )}
      {part.state === "done" && output === undefined && part.output !== undefined && (
        <StepCodeBlock value={part.output} />
      )}
    </Step>
  );
}
